// MARK://dhk8/admin-shell/pages/modules/CatalogModifiersPage
// Bug #96 — admin UI for dhk8-catalog-modifiers. Lives at /catalog/modifiers
// inside the AdminLayout shell. Talks to dhk8-catalog-modifiers via the
// ../../lib/clients/modifiers.ts client. Re-uses the same `adm-*` design
// tokens / Sheet pattern as CatalogPage.tsx so it inherits the brand surface
// (no @dockethub/ui import — the admin app composes plain elements with the .adm-*
// classes defined in styles.css; @dockethub/ui isn't a dependency of admin-shell).
//
// Barrido fila→ficha (Gustavo 2026-06-23): la lista es ahora una vista de overview.
// Cada grupo es una FILA que navega a su ficha (/catalog/modifiers/:id) donde viven
// los tabs General + Opciones + Historial y el botón Editar. "Nuevo grupo" se queda
// como sheet aquí (la creación no migra). La gestión de opciones, vínculo a productos,
// activar/desactivar, editar y eliminar del grupo se movieron a la ficha de detalle.
//
// Features:
//   - List all modifier groups with their options preview (read-only)
//   - Filter/search by name
//   - Create new group (name, minSelect, maxSelect, isRequired) → sheet
//   - Row click → ficha de detalle del grupo
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, X, ChevronRight } from 'lucide-react'

import * as mod from '../../lib/clients/modifiers.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import StatusPill, { lifecycleTone } from '../../components/StatusPill.js'
import { useToast } from '../../components/Toast.js'

interface GroupForm {
  name: string
  minSelect: number
  maxSelect: number
  isRequired: boolean
}

const BLANK_GROUP: GroupForm = { name: '', minSelect: 0, maxSelect: 1, isRequired: false }

export default function CatalogModifiersPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const groups = useAsync((s) => mod.listModifierGroups({}, s))

  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [groupForm, setGroupForm] = useState<GroupForm>(BLANK_GROUP)
  const [groupBusy, setGroupBusy] = useState(false)

  const filteredGroups = useMemo(() => {
    const all = groups.data?.items ?? []
    const q = query.trim().toLowerCase()
    if (!q) return all
    return all.filter((g) =>
      g.name.toLowerCase().includes(q) ||
      g.options.some((o) => o.label.toLowerCase().includes(q)),
    )
  }, [groups.data, query])

  // ── group create (sheet) ─────────────────────────────────────────────────
  function openGroupCreate() {
    setGroupForm({ ...BLANK_GROUP })
    setCreating(true)
  }
  async function saveGroup() {
    if (!groupForm.name.trim()) return
    setGroupBusy(true)
    try {
      const created = await mod.createModifierGroup({
        name: groupForm.name.trim(),
        minSelect: groupForm.minSelect,
        maxSelect: groupForm.maxSelect,
        isRequired: groupForm.isRequired,
      })
      toast('Grupo creado', 'success')
      setCreating(false)
      groups.reload()
      // Lleva al usuario directo a la ficha del grupo recién creado para que
      // agregue opciones / vincule productos.
      navigate(`/catalog/modifiers/${created.id}`)
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo guardar el grupo.', 'danger')
    } finally {
      setGroupBusy(false)
    }
  }

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <h1 className="adm-page__title">Modificadores</h1>
          <p className="adm-page__sub">Grupos de modificadores y opciones (extras, términos, sustituciones). Abre un grupo para gestionar sus opciones y vincularlo a productos.</p>
        </div>
      </div>

      <div className="adm-page__actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="adm-btn adm-btn--primary" onClick={openGroupCreate}>
          <Plus size={16} /> Nuevo grupo
        </button>
        <label className="adm-field" style={{ flex: 1, maxWidth: 320, margin: 0 }}>
          <span className="adm-field__label" style={{ display: 'none' }}>Buscar</span>
          <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, color: 'var(--pos-fg-tertiary)' }} aria-hidden />
            <input
              className="adm-input"
              placeholder="Buscar por nombre del grupo u opción…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
            {query && (
              <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => setQuery('')} style={{ position: 'absolute', right: 4 }} aria-label="Limpiar búsqueda">
                <X size={14} />
              </button>
            )}
          </span>
        </label>
      </div>

      {asyncState(groups.loading, groups.error, groups.reload)}

      {groups.data && (
        <div className="adm-card adm-card--pad0">
          <div className="adm-list">
            {filteredGroups.length === 0 && <div className="adm-empty">Sin grupos de modificadores</div>}
            {filteredGroups.map((g) => {
              const preview = g.options.slice(0, 4).map((o) => o.label).join(' · ')
              const more = g.options.length - 4
              return (
                <button
                  key={g.id}
                  type="button"
                  className="adm-row"
                  onClick={() => navigate(`/catalog/modifiers/${g.id}`)}
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}
                >
                  <div className="adm-row__main">
                    <div className="adm-row__title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>{g.name}</span>
                      <StatusPill tone={lifecycleTone(g.active)} label={g.active ? 'Activo' : 'Inactivo'} />
                      {g.isRequired && <StatusPill tone="warn" label="Obligatorio" />}
                    </div>
                    <div className="adm-row__meta">
                      Selección: {g.minSelect}–{g.maxSelect} · {g.options.length} opción{g.options.length === 1 ? '' : 'es'}
                      {preview && <> · {preview}{more > 0 ? ` +${more}` : ''}</>}
                    </div>
                  </div>
                  <div className="adm-row__tail">
                    <ChevronRight size={18} style={{ color: 'var(--pos-fg-tertiary)' }} aria-hidden />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Group create sheet ───────────────────────────────────────────── */}
      <SectionedSheet
        open={creating}
        title="Nuevo grupo de modificadores"
        onClose={() => setCreating(false)}
        footer={
          <>
            <button className="adm-btn" onClick={() => setCreating(false)} disabled={groupBusy}>Cancelar</button>
            <button className="adm-btn adm-btn--primary" onClick={saveGroup} disabled={!groupForm.name.trim() || groupBusy}>
              {groupBusy ? 'Guardando…' : 'Guardar'}
            </button>
          </>
        }
      >
        <FormSection title="Datos básicos">
          <label className="adm-field">
            <span className="adm-field__label">Nombre</span>
            <input className="adm-input" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="Extras / Término / Salsa…" autoFocus />
          </label>
        </FormSection>

        <FormSection title="Reglas de selección">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label className="adm-field">
              <span className="adm-field__label">Mínimo</span>
              <input
                className="adm-input"
                type="number"
                min={0}
                value={groupForm.minSelect}
                onChange={(e) => setGroupForm({ ...groupForm, minSelect: Math.max(0, Number(e.target.value) || 0) })}
              />
            </label>
            <label className="adm-field">
              <span className="adm-field__label">Máximo</span>
              <input
                className="adm-input"
                type="number"
                min={1}
                value={groupForm.maxSelect}
                onChange={(e) => setGroupForm({ ...groupForm, maxSelect: Math.max(1, Number(e.target.value) || 1) })}
              />
            </label>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={groupForm.isRequired} onChange={(e) => setGroupForm({ ...groupForm, isRequired: e.target.checked })} />
            Obligatorio (el cajero no puede saltarlo)
          </label>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
